"use client";

import { useState } from "react";
import Link from "next/link";
import {
    ArrowLeft, Send, CheckCircle, Loader2,
    MessageCircle, Mail, Clock, Zap, Shield, BookOpen,
    ChevronRight, Phone,
} from "lucide-react";
import { Logo } from "@/components/ui/Logo";

import { HORARIO_ATENCION, RESPUESTA_SOPORTE, WHATSAPP } from "@/lib/marca";
import { PRESTADOR } from "@/lib/legal/prestador";

const CATEGORIES = [
    "Problema técnico",
    "Pregunta sobre facturación",
    "Suscripción / Pagos",
    "General",
];

/**
 * Los tres datos del encabezado, todos leidos de su fuente.
 *
 * Estaban escritos a mano y dos de los tres no se sostenian: «< 2 h de respuesta promedio» y
 * «100% respuestas garantizadas» no se miden en ninguna parte, y «L–V» siguio diciendo viernes
 * despues de que el horario pasara a sabado — contradiciendo al bloque de horario de esta misma
 * pantalla, quince lineas mas abajo.
 */
const STATS = [
    { icon: Zap,    value: RESPUESTA_SOPORTE.corto,   label: "Tiempo de respuesta" },
    { icon: Shield, value: "Español",                 label: "Atención local" },
    { icon: Clock,  value: HORARIO_ATENCION.diasCorto, label: HORARIO_ATENCION.horas },
];

export default function SoportePage() {
    const [name, setName]         = useState("");
    const [email, setEmail]       = useState("");
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [message, setMessage]   = useState("");
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState("");
    const [sent, setSent]         = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!name.trim())                          { setError("Por favor ingresa tu nombre."); return; }
        if (!email.trim() || !email.includes("@")) { setError("Ingresa un correo válido."); return; }
        if (message.trim().length < 10)            { setError("El mensaje debe tener al menos 10 caracteres."); return; }

        setLoading(true);
        setError("");
        try {
            const res = await fetch("/api/soporte", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, category, message }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Error desconocido");
            setSent(true);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Error al enviar. Intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen bg-slate-50">

            {/* ── Hero ─────────────────────────────────────────────── */}
            <div className="bg-[#1E3A8A] relative overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 pointer-events-none" />
                <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />

                {/* Nav */}
                <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-5 pb-3 flex items-center justify-between gap-4">
                    <Link href="/" className="flex items-center gap-2 text-blue-200 hover:text-white transition text-sm whitespace-nowrap shrink-0">
                        <ArrowLeft className="w-4 h-4" /> Volver al inicio
                    </Link>
                    <Logo size="md" dark className="opacity-90 shrink-0" />
                </div>

                {/* Hero copy */}
                <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-6 pb-4 text-center">
                    <div className="inline-flex items-center gap-2 bg-white/10 text-blue-100 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                        <Phone className="w-3.5 h-3.5" /> Soporte en español
                    </div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-3">
                        ¿En qué podemos ayudarte?
                    </h1>
                    <p className="text-blue-200 text-sm md:text-base max-w-lg mx-auto">
                        Nuestro equipo está disponible para resolver tus dudas sobre facturación electrónica, DTE y el uso de la plataforma.
                    </p>

                    {/* Stats */}
                    <div className="flex flex-wrap justify-center gap-3 sm:gap-5 mt-8 pb-10">
                        {STATS.map(({ icon: Icon, value, label }) => (
                            <div key={label} className="flex items-center gap-2.5 bg-white/10 rounded-2xl px-4 sm:px-5 py-3">
                                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                                    <Icon className="w-4 h-4 text-white" />
                                </div>
                                <div className="text-left">
                                    <p className="text-white font-bold text-sm leading-none">{value}</p>
                                    <p className="text-blue-200 text-xs mt-0.5">{label}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Main content ─────────────────────────────────────── */}
            <div className="max-w-5xl mx-auto px-4 sm:px-8 mt-8 pb-16">
                <div className="grid md:grid-cols-5 gap-6 items-start">

                    {/* ── Left sidebar ──────────────────────────────── */}
                    <div className="md:col-span-2 space-y-4">

                        {/* WhatsApp */}
                        <a
                            href={WHATSAPP.enlace("Hola, necesito ayuda con Factura Premium")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-md shadow-emerald-200 group-hover:scale-105 transition-transform">
                                <MessageCircle className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-slate-900">WhatsApp</p>
                                <p className="text-xs text-slate-500 truncate">{WHATSAPP.visible}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                        </a>

                        {/* Email */}
                        <a
                            href={`mailto:${PRESTADOR.correo}`}
                            className="group flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-blue-200 transition-all"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] flex items-center justify-center shrink-0 shadow-md shadow-blue-200 group-hover:scale-105 transition-transform">
                                <Mail className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-slate-900">Correo directo</p>
                                <p className="text-xs text-slate-500 truncate">{PRESTADOR.correo}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                        </a>

                        {/* Guide */}
                        <Link
                            href="/dashboard/guia"
                            className="group flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg hover:border-indigo-200 transition-all"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-indigo-500 flex items-center justify-center shrink-0 shadow-md shadow-indigo-200 group-hover:scale-105 transition-transform">
                                <BookOpen className="w-6 h-6 text-white" />
                            </div>
                            {/* Esta pagina es publica y la guia vive dentro del panel: a quien
                                todavia no tiene cuenta lo manda al login sin explicarle por que.
                                El aviso evita que parezca un enlace roto. */}
                            <div className="flex-1">
                                <p className="text-sm font-bold text-slate-900">Guía del sistema</p>
                                <p className="text-xs text-slate-500">Manual completo · requiere iniciar sesión</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                        </Link>

                        {/* Hours */}
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                            <div className="flex items-center gap-2 mb-2">
                                <Clock className="w-4 h-4 text-[#1E3A8A]" />
                                <p className="text-xs font-bold text-[#1E3A8A] uppercase tracking-wider">Horario de atención</p>
                            </div>
                            <p className="text-sm font-semibold text-slate-800">{HORARIO_ATENCION.dias}</p>
                            <p className="text-sm text-slate-700">{HORARIO_ATENCION.horas}</p>
                            <p className="text-xs text-blue-600 mt-1.5 font-medium">{HORARIO_ATENCION.zona}</p>
                        </div>
                    </div>

                    {/* ── Contact form ──────────────────────────────── */}
                    <div className="md:col-span-3">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                            {/* Form header */}
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                                    <Send className="w-4 h-4 text-[#1E3A8A]" />
                                </div>
                                <h2 className="text-base font-bold text-slate-900">Envíanos un mensaje</h2>
                            </div>

                            <div className="p-6">
                                {sent ? (
                                    <div className="flex flex-col items-center text-center gap-4 py-10">
                                        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                                            <CheckCircle className="w-10 h-10 text-emerald-500" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold text-slate-900">¡Mensaje enviado!</h3>
                                            <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                                                Hemos recibido tu mensaje. Te responderemos a{" "}
                                                <strong className="text-slate-700">{email}</strong> lo antes posible.
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => { setSent(false); setName(""); setEmail(""); setMessage(""); }}
                                            className="mt-2 px-6 py-2.5 rounded-xl bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-blue-900 transition-colors"
                                        >
                                            Enviar otro mensaje
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                                                    Nombre completo
                                                </label>
                                                <input
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    placeholder="Tu nombre"
                                                    className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                                                    Correo electrónico
                                                </label>
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="tu@correo.com"
                                                    className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-colors"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                                                Categoría del mensaje
                                            </label>
                                            <select
                                                value={category}
                                                onChange={(e) => setCategory(e.target.value)}
                                                className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-colors"
                                            >
                                                {CATEGORIES.map((c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">
                                                Mensaje
                                            </label>
                                            <textarea
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                placeholder="Describe tu consulta o problema con el mayor detalle posible…"
                                                rows={5}
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-colors resize-none"
                                            />
                                        </div>

                                        {error && (
                                            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">
                                                {error}
                                            </p>
                                        )}

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full h-12 rounded-xl bg-[#1E3A8A] hover:bg-blue-900 text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60 shadow-md shadow-blue-900/20"
                                        >
                                            {loading
                                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</>
                                                : <><Send className="w-4 h-4" /> Enviar mensaje</>
                                            }
                                        </button>

                                        <p className="text-center text-xs text-slate-400">
                                            Al enviar aceptas nuestra{" "}
                                            <Link href="/privacidad" className="underline hover:text-slate-600">Política de Privacidad</Link>
                                        </p>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom links */}
                <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-500">
                    <Link href="/terminos"  className="hover:text-blue-600 hover:underline transition-colors">Términos de Servicio</Link>
                    <Link href="/privacidad" className="hover:text-blue-600 hover:underline transition-colors">Política de Privacidad</Link>
                    <Link href="/"          className="hover:text-blue-600 hover:underline transition-colors">Volver al inicio</Link>
                </div>
            </div>
        </div>
    );
}
